<template>
  <div class="">
    <h3 class="display-4 text-primary heading tw-flex tw-flex-col tw-gap-2 tw-text-center tw-mb-10">
      <span>FAQs on Insurance</span> <span>Reimbursement and Receipts</span>
    </h3>

    <ul class="tw-list-none faqs-container">
      <li
        v-for="(faq, index) in faqs"
        :key="index"
        class="tw-border-b tw-py-5 tw-px-2 tw-border-silver-gray"
      >
        <div
          class="tw-flex tw-justify-between tw-items-center tw-select-none tw-cursor-pointer tw-text-2xl"
          @click="onClickFaq(index)"
        >
          {{ faq.question }}

          <div>
            <IconMinus v-if="faqDropdownStatus[index]" />
            <IconPlus v-else />
          </div>
        </div>

        <div
          v-show="faqDropdownStatus[index]"
          v-html-safe="faq.answer"
          class="tw-mt-3 tw-text-gray-700"
        >
          <!-- The answer will be rendered here -->
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { IconPlus, IconMinus } from '@/components'

export default {
  components: {
    IconPlus,
    IconMinus,
  },
  data() {
    return {
      faqDropdownStatus: {}
    }
  },
  computed: {
    faqs() {
      return [
        {
          question: 'What should I do if my insurance claim is denied?',
          answer: 'Although Winona does not provide assistance with insurance related issues, many patients have successfully appealed their claims. According to our patients, they simply submitted the information again, and the claim was approved. Keep in mind that this may not work for all insurance companies.'
        },
        {
          question: 'What documentation is required for insurance reimbursement?',
          answer: `
            <p>
              <span style="white-space:pre-wrap;">To improve the likelihood of claim approval, you may need to provide:</span>
            </p>
            <ul>
                <li>
                    <span style="white-space:pre-wrap;">ICD-10, CPT, or NDC codes</span>
                </li>
                <li>
                    <span style="white-space:pre-wrap;">NPI (National Provider Identifier) numbers</span>
                </li>
                <li>
                    <span style="white-space:pre-wrap;">Winona health receipts with prescription information</span>
                </li>
            </ul>
            <p>
                <span style="white-space:pre-wrap;">If you’re unsure which codes or information to include, we recommend submitting all of the available documentation just in case. Each insurance plan has their own requirements for reimbursement of medical expenses. We recommend that you check with your plan administrator for more details.&nbsp;</span><br>
                <span style="white-space:pre-wrap;">We provide three different receipts that contain the information that most insurance companies require for reimbursement. We recommend submitting all receipts with your claim to improve the likelihood of approval.</span>
            </p>
          `
        },
        {
          question: 'Can I get single transaction receipts instead of all receipts in one file?',
          answer: 'We’re currently in the process of making individual receipts available for download directly from your account. In the meantime, we recommend using Adobe, SplitPDF, or LovePDF to split the full PDF.'
        },
        {
          question: 'Can I receive receipt information via email?',
          answer: 'Due to HIPAA regulations, we cannot email sensitive health information without redacting or blocking certain details. However, all receipts are easily downloaded from the Patient Portal in a PDF format and can be securely submitted electronically along with your insurance form(s).'
        },
        {
          question: 'Are there any tips for navigating insurance reimbursement portals?',
          answer: `
            <p>
                <span style="white-space:pre-wrap;">To avoid delays and confusion, ensure you:</span>
            </p>
            <ul>
                <li>
                    <span style="white-space:pre-wrap;">Upload all required documents at once</span>
                </li>
                <li>
                    <span style="white-space:pre-wrap;">Use file formats accepted by your insurance portal (e.g., PDF)</span>
                </li>
                <li>
                    <span style="white-space:pre-wrap;">Follow any instructions provided by your insurance company regarding documentation</span>
                </li>
            </ul>
          `
        },
        {
          question: 'What if my insurance provider outsources prescription claims?',
          answer: 'Some insurance providers, such as Blue Cross Blue Shield (Massachusetts), may use third-party vendors like CVS for prescription claims. This can create additional steps in the reimbursement process. Be sure to follow their specific procedures for submitting claims to these vendors.'
        },
        {
          question: 'How long does reimbursement take?',
          answer: 'Turnaround times can vary widely. Submitting complete and accurate documentation often leads to faster processing times.'
        },
        {
          question: 'Can submitting my medical receipts affect my eligibility for other benefits?',
          answer: 'Yes! In some cases, submitting medical receipts increases other benefits. For example, patients on food stamps or SNAP have seen their benefits increase from $15–$20 to nearly $100 after submitting receipts for medical services.'
        },
        {
          question: 'How can I provide feedback on the reimbursement process?',
          answer: 'We value your input! You can participate in surveys and focus groups or contact customer support to share your experiences and suggestions for improvements. Contact hello@bywinona.com for more information.'
        },
        {
          question: 'What are ICD-10, CPT, and NDC codes, and where can they be found?',
          answer: `
          <p>ICD Codes (International Classification of Diseases):</p>
          <ul>
            <li>These are used to describe the medical condition being treated, such as menopause or hormone deficiencies.</li>
            <li>This code explains why you require HRT.</li>
            <li>It will appear on&nbsp;the HSA receipt in your Patient Portal like this: &ldquo;ICD-10 code N95.9 - Menopausal and perimenopausal disorder, unspecified.&rdquo;</li>
          </ul>
          <p>NPI Codes (National Provider Identifier):</p>
          <ul>
            <li>This code identifies the healthcare provider administering your HRT.</li>
            <li>It tells the insurance provider who delivered the HRT service.</li>
            <li>It can be found on the HSA receipt in your Patient Portal.</li>
          </ul>
          <p>NDC Codes (National Drug Code):</p>
          <ul>
            <li>These identify the specific hormone medication prescribed or dispensed, including dosage and manufacturer details.</li>
            <li>Example: 00054-0247-13 might refer to a particular estrogen patch used in HRT.</li>
            <li>This code provides details on which drugs were provided as treatment.</li>
            <li>These can be found on the NDC receipt/document in your Patient Portal.</li>
          </ul>
          `
        },
        {
          question: 'Does Winona provide a Letter of Medical Necessity?',
          answer: 'Yes, you can view and download a Letter of Medical Necessity (LMN) from your Patient Portal at any time. This form is only available to patients with active prescriptions. This form is not available in advance for pre-authorization with insurance companies or FSA/HSA administrators.'
        },
      ]
    }
  },
  methods: {
    onClickFaq(index) {
      this.$set(this.faqDropdownStatus, index, !this.faqDropdownStatus[index]);
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .faqs-container ul {
  list-style: disc;
  padding-left: 25px;
  margin-bottom: 20px ;
}
</style>